import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Select,
  Link,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Common/Loader';
import { getOrganisationGroups, getGroupCountries, createGroup, updateGroup } from '../../services/api';

const emptyDraft = () => ({ name_english: '', name_native: '', country_id: '', status: 1 });

const CreateGroup = () => {
  const { selectedOrganisation, refreshUser } = useAuth();

  const [groups, setGroups] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newRow, setNewRow] = useState(emptyDraft());
  const [newRowError, setNewRowError] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft());
  const [editRowError, setEditRowError] = useState('');
  const [saving, setSaving] = useState(false);

  const [statusFilter, setStatusFilter] = useState('all');

  const [notesGroup, setNotesGroup] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSynced, setNotesSynced] = useState(''); // last value we know came from the server
  const [savingNotes, setSavingNotes] = useState(false);

  const load = useCallback(async () => {
    if (!selectedOrganisation?.id) return;
    setLoading(true);
    // Independent, not Promise.all — a hiccup fetching one (e.g. a transient DB
    // connection drop) must not wipe out data that already loaded fine for the other.
    const [groupsResult, countriesResult] = await Promise.allSettled([
      getOrganisationGroups(selectedOrganisation.id),
      getGroupCountries()
    ]);

    if (groupsResult.status === 'fulfilled' && groupsResult.value.success) {
      setGroups(groupsResult.value.data);
    } else if (groupsResult.status === 'rejected') {
      setError(groupsResult.reason?.response?.data?.error || 'Failed to load groups');
    }

    if (countriesResult.status === 'fulfilled' && countriesResult.value.success) {
      setCountries(countriesResult.value.data);
    } else if (countriesResult.status === 'rejected') {
      setError('Failed to load the country list — try refreshing the page');
    }

    setLoading(false);
  }, [selectedOrganisation?.id]);

  useEffect(() => { load(); }, [load]);

  // Groups can be created/edited from elsewhere (another admin, the 525482 admin
  // panel directly, or db_525482) — poll quietly in the background so this list
  // stays live without a manual refresh. This keeps running even while a row is
  // being edited or the Notes popup is open: editDraft/notesDraft are held in their
  // own state, not derived from `groups`, so refreshing `groups` here can't clobber
  // in-progress typing — see the effect below for how the open Notes popup itself
  // picks up an externally-changed value. Skipped only while there's no token, so a
  // torn-down session doesn't spam 401s.
  useEffect(() => {
    if (!selectedOrganisation?.id) return;
    const interval = setInterval(() => {
      if (!localStorage.getItem('token')) return;
      getOrganisationGroups(selectedOrganisation.id)
        .then(res => { if (res.success) setGroups(res.data); })
        .catch(err => console.error('Background group refresh failed:', err.message));
    }, 15000);
    return () => clearInterval(interval);
  }, [selectedOrganisation?.id]);

  const handleCreate = async () => {
    if (!newRow.name_english.trim()) {
      setNewRowError('Group name is required');
      return;
    }
    setCreating(true);
    try {
      const res = await createGroup({
        name_english: newRow.name_english.trim(),
        name_native: newRow.name_native.trim() || null,
        country_id: newRow.country_id || null
      });
      setNewRow(emptyDraft());
      setNewRowError('');
      const parts = [];
      if (res.data?.advertCreated) parts.push('advert');
      if (res.data?.usersCreated) parts.push(`${res.data.usersCreated.toLocaleString()} users`);
      setSuccess(`Group created${parts.length ? ` — auto-created ${parts.join(' and ')}` : ''}`);
      await Promise.all([load(), refreshUser()]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (group) => {
    setEditingId(group.id);
    setEditDraft({
      name_english: group.name_english || '',
      name_native: group.name_native || '',
      country_id: group.country_id || '',
      status: Number(group.status) === 0 ? 0 : 1
    });
    setEditRowError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditRowError('');
  };

  const saveEdit = async (group) => {
    if (!editDraft.name_english.trim()) {
      setEditRowError('Group name is required');
      return;
    }
    setSaving(true);
    try {
      await updateGroup(group.id, {
        name_english: editDraft.name_english.trim(),
        name_native: editDraft.name_native.trim() || null,
        country_id: editDraft.country_id || null,
        status: editDraft.status
      });
      setEditingId(null);
      setSuccess('Group updated');
      await Promise.all([load(), refreshUser()]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update group');
    } finally {
      setSaving(false);
    }
  };

  const openNotes = (group) => {
    setNotesGroup(group);
    setNotesDraft(group.notes || '');
    setNotesSynced(group.notes || '');
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await updateGroup(notesGroup.id, { notes: notesDraft });
      setNotesGroup(null);
      setSuccess('Notes saved');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // While the Notes popup is open, the background poll above keeps refreshing
  // `groups` underneath it — pick up a value changed elsewhere (e.g. the 525482
  // admin panel, in another tab) and reflect it live. Only auto-applies if the
  // textbox still shows what we last synced, i.e. the admin hasn't started typing
  // their own edit — that always wins over an incoming external change.
  useEffect(() => {
    if (!notesGroup) return;
    const fresh = groups.find((g) => g.id === notesGroup.id);
    if (!fresh) return;
    const freshNotes = fresh.notes || '';
    if (freshNotes !== notesSynced) {
      if (notesDraft === notesSynced) setNotesDraft(freshNotes);
      setNotesSynced(freshNotes);
      setNotesGroup(fresh);
    }
  }, [groups, notesGroup, notesDraft, notesSynced]);

  const filteredGroups = groups.filter((g) => {
    if (statusFilter === 'all') return true;
    const isActive = Number(g.status) === 1;
    return statusFilter === 'active' ? isActive : !isActive;
  });

  const colWidths = { name: 260, native: 220, country: 220 };

  const countrySelect = (value, onChange) => (
    <Select
      size="small"
      displayEmpty
      disabled={countries.length === 0}
      sx={{ width: colWidths.country }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <MenuItem value="">
        <Typography color="text.secondary">
          {countries.length === 0 ? 'Country list unavailable' : 'No country'}
        </Typography>
      </MenuItem>
      {countries.map((c) => (
        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
      ))}
    </Select>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography variant="h4" fontWeight={600}>Groups</Typography>
          {!loading && (
            <Typography variant="h6" color="text.secondary" fontWeight={400}>
              ({filteredGroups.length})
            </Typography>
          )}
        </Box>
        <RadioGroup
          row
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
          <FormControlLabel value="active" control={<Radio size="small" />} label="Active" />
          <FormControlLabel value="paused" control={<Radio size="small" />} label="Paused" />
        </RadioGroup>
      </Box>

      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Loader />
        ) : (
          <>
            {/* Column headers */}
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ width: colWidths.country }}>Country</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.name }}>Group Name</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.native }}>Native Name</Typography>
            </Box>

            {/* New row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              {countrySelect(newRow.country_id, (v) => setNewRow({ ...newRow, country_id: v }))}
              <TextField
                size="small"
                placeholder="Group name"
                sx={{ width: colWidths.name }}
                value={newRow.name_english}
                onChange={(e) => { setNewRow({ ...newRow, name_english: e.target.value }); setNewRowError(''); }}
                error={!!newRowError}
                helperText={newRowError || ''}
                disabled={creating}
              />
              <TextField
                size="small"
                placeholder="Native name (optional)"
                sx={{ width: colWidths.native }}
                value={newRow.name_native}
                onChange={(e) => setNewRow({ ...newRow, name_native: e.target.value })}
                disabled={creating}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                {creating && <CircularProgress size={16} />}
                <Link component="button" underline="hover" onClick={handleCreate} disabled={creating}>
                  {creating ? 'Creating…' : 'New'}
                </Link>
              </Box>
            </Box>

            {/* Existing rows */}
            {filteredGroups.map((group) => {
              const isEditing = editingId === group.id;
              return (
                <Box key={group.id} sx={{ display: 'flex', alignItems: isEditing ? 'flex-start' : 'center', gap: 2, py: 1, borderBottom: '1px solid #f0f0f0' }}>
                  {isEditing ? (
                    <>
                      {countrySelect(editDraft.country_id, (v) => setEditDraft({ ...editDraft, country_id: v }))}
                      <TextField
                        size="small"
                        sx={{ width: colWidths.name }}
                        value={editDraft.name_english}
                        onChange={(e) => { setEditDraft({ ...editDraft, name_english: e.target.value }); setEditRowError(''); }}
                        error={!!editRowError}
                        helperText={editRowError || ''}
                        disabled={saving}
                      />
                      <TextField
                        size="small"
                        sx={{ width: colWidths.native }}
                        value={editDraft.name_native}
                        onChange={(e) => setEditDraft({ ...editDraft, name_native: e.target.value })}
                        disabled={saving}
                      />
                      <FormControlLabel
                        sx={{ mt: 0.5 }}
                        control={
                          <Switch
                            size="small"
                            checked={editDraft.status === 1}
                            onChange={(e) => setEditDraft({ ...editDraft, status: e.target.checked ? 1 : 0 })}
                          />
                        }
                        label={editDraft.status === 1 ? 'Active' : 'Paused'}
                      />
                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Link component="button" underline="hover" onClick={() => saveEdit(group)}>Save</Link>
                        <Link component="button" underline="hover" onClick={() => openNotes(group)}>
                          Notes{group.notes ? ' •' : ''}
                        </Link>
                        <Link component="button" underline="hover" onClick={cancelEdit}>Cancel</Link>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ width: colWidths.country }}>{group.country_name || '-'}</Typography>
                      <Box sx={{ width: colWidths.name, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{group.name_english}</Typography>
                        {Number(group.status) !== 1 && (
                          <Chip label="Paused" size="small" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ width: colWidths.native }}>{group.name_native || '-'}</Typography>
                      <Link component="button" underline="hover" onClick={() => startEdit(group)}>Edit</Link>
                    </>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </Paper>

      {/* Notes popup */}
      <Dialog
        open={!!notesGroup}
        onClose={() => !savingNotes && setNotesGroup(null)}
        maxWidth="xs"
        fullWidth
        sx={{ '& .MuiDialog-paper': { maxWidth: 666 } }} // 150% of the default "xs" 444px
      >
        <DialogTitle>Notes — {notesGroup?.name_english}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            minRows={12}
            fullWidth
            placeholder="Add notes about this group..."
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            disabled={savingNotes}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesGroup(null)} disabled={savingNotes}>Cancel</Button>
          <Button variant="contained" onClick={saveNotes} disabled={savingNotes}>
            {savingNotes ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={6000}
        onClose={() => { setSuccess(''); setError(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSuccess(''); setError(''); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateGroup;
